import * as SelectPrimitive from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { cn } from '../lib/utils';
import { useKeyboardOnlyReturnFocus } from '../hooks/useKeyboardOnlyReturnFocus';

function Select({ dataTestId, ...props }) {
  return <SelectPrimitive.Root data-slot="select" data-testid={dataTestId} {...props} />;
}

function SelectGroup({ dataTestId, ...props }) {
  return <SelectPrimitive.Group data-slot="select-group" data-testid={dataTestId} {...props} />;
}

function SelectValue({ className, dataTestId, ...props }) {
  // A long selected label (country/region names especially — "Saint Vincent and the
  // Grenadines", "Australian Capital Territory") was overflowing the trigger instead of
  // clipping, because neither this nor the trigger declared how to handle overflow. `block` so
  // truncate's max-width actually applies (Radix's Value renders inline by default).
  return (
    <SelectPrimitive.Value
      data-slot="select-value"
      data-testid={dataTestId}
      className={cn('block truncate', className)}
      {...props}
    />
  );
}

function SelectTrigger({ className, children, dataTestId, ...props }) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-testid={dataTestId}
      className={cn(
        'border-input data-[placeholder]:text-fg-placeholder-select [&_svg:not([class*=\'size-\'])]:size-4 flex w-full items-center justify-between gap-2 rounded-lg border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      {...props}
    >
      {/* min-w-0 lets this flex child actually shrink below its content width — without it,
          a long value pushes the chevron out of the trigger instead of truncating. */}
      <span className="min-w-0 flex-1 overflow-hidden text-left">{children}</span>
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="size-4 opacity-50 shrink-0" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  position = 'popper',
  dataTestId,
  onPointerDown,
  onPointerUp,
  onKeyDown,
  onEscapeKeyDown,
  onPointerDownOutside,
  onCloseAutoFocus,
  ...props
}) {
  const returnFocusProps = useKeyboardOnlyReturnFocus({
    onPointerDown,
    onPointerUp,
    onKeyDown,
    onEscapeKeyDown,
    onPointerDownOutside,
    onCloseAutoFocus,
  });
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        data-testid={dataTestId}
        className={cn(
          'bg-popover text-popover-foreground relative z-50 max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border border-line-menu shadow-md',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className
        )}
        position={position}
        {...returnFocusProps}
        {...props}
      >
        <SelectPrimitive.ScrollUpButton className="flex cursor-default items-center justify-center py-1">
          <ChevronUpIcon className="size-4" />
        </SelectPrimitive.ScrollUpButton>
        <SelectPrimitive.Viewport
          className={cn(
            'p-1',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectPrimitive.ScrollDownButton className="flex cursor-default items-center justify-center py-1">
          <ChevronDownIcon className="size-4" />
        </SelectPrimitive.ScrollDownButton>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({ className, dataTestId, ...props }) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      data-testid={dataTestId}
      className={cn('text-muted-foreground px-2 py-1.5 text-xs', className)}
      {...props}
    />
  );
}

function SelectItem({ className, children, dataTestId, ...props }) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      data-testid={dataTestId}
      className={cn(
        'focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default select-none items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className
      )}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({ className, dataTestId, ...props }) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      data-testid={dataTestId}
      className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
      {...props}
    />
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
